import { useState } from "react";
import { useEffect } from "react";
import { Line } from "react-chartjs-2";
import { Bar } from "react-chartjs-2";
import { Doughnut } from "react-chartjs-2";

import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import "chart.js/auto";

import constants from "../../assets/constants";
import { extractAnalyticsDateLabels, getGraphColor } from "./graphUtils";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const BotpressUseageGraph = ({ records, botpressGraphType, setTitleText, colorIndex = 0 }) => {
  const theme = useTheme();

  const generateGraphData = (analyticsRecords, graphType) => {
    if (analyticsRecords == null) {
      return null;
    }

    //  Default data structure
    //  const emptyData = {
    //    startDateTimeUtc: searchDate.toISOString(),
    //    endDateTimeUtc: searchDate.toISOString(),
    //    returningUsers: 0,
    //    newUsers: 0,
    //    sessions: 0,
    //    userMessages: 0,
    //    botMessages: 0,
    //    events: 0,
    //    eventTypes: {
    //      "webchat:trigger": 0,
    //      "webchat:conversationStarted": 0,
    //      state_expired: 0,
    //    },
    //    customEvents: {},
    //    llm: {
    //      calls: 0,
    //      errors: 0,
    //      inputTokens: 0,
    //      outputTokens: 0,
    //      latency: {
    //        mean: 0,
    //        sd: 0,
    //        min: 0,
    //        max: 0,
    //      },
    //      tokensPerSecond: {
    //        mean: 0,
    //        sd: 0,
    //        min: 0,
    //        max: 0,
    //      },
    //      cost: {
    //        sum: 0,
    //        mean: 0,
    //        sd: 0,
    //        min: 0,
    //        max: 0,
    //      },
    //    },
    //    messages: 0,
    //  };

    let labels = []; // extractAnalyticsDateLabels(analyticsRecords);
    let dataSets = [];

    if (graphType === constants.botpressGraphTypes.usersGraph) {
      labels = extractAnalyticsDateLabels(analyticsRecords);
      let x = colorIndex;
      let xPlusOne = x + 2;
      dataSets.push({
        label: "Returning Users",
        data: analyticsRecords.map(record => record.returningUsers),
        borderColor: getGraphColor(theme, x),
        backgroundColor: context => {
          const { ctx, chartArea } = context.chart;

          if (!chartArea) {
            // This ensures the chart has been initialized before drawing
            return null;
          }

          // Create a vertical gradient (x0, y0, x1, y1)
          const gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);

          // Add color stops (0 is bottom, 1 is top)
          gradient.addColorStop(0, alpha(theme.palette.commonground.main, 1.0)); // Transparent at bottom
          gradient.addColorStop(1, getGraphColor(theme, x, 0.5)); // Semi-opaque at top

          return gradient;
        },
        borderWidth: 2,
        fill: true,
        tension: 0.4,
        pointStyle: false, // Removes point rendering
      });
      dataSets.push({
        label: "New Users",
        data: analyticsRecords.map(record => record.newUsers),
        borderColor: getGraphColor(theme, xPlusOne),
        backgroundColor: context => {
          const { ctx, chartArea } = context.chart;

          if (!chartArea) {
            // This ensures the chart has been initialized before drawing
            return null;
          }

          // Create a vertical gradient (x0, y0, x1, y1)
          const gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);

          // Add color stops (0 is bottom, 1 is top)
          gradient.addColorStop(0, alpha(theme.palette.commonground.main, 1.0)); // Transparent at bottom
          gradient.addColorStop(1, getGraphColor(theme, xPlusOne, 0.5)); // Semi-opaque at top

          return gradient;
        },
        borderWidth: 2,
        fill: true,
        tension: 0.4,
        pointStyle: false, // Removes point rendering
      });

      if (setTitleText) {
        const totalReturning = analyticsRecords.reduce(
          (sum, record) => sum + record.returningUsers,
          0,
        );
        const totalNew = analyticsRecords.reduce((sum, record) => sum + record.newUsers, 0);
        setTitleText(`${totalReturning + totalNew} Users`);
      }
    } else if (graphType === constants.botpressGraphTypes.userTypesGraph) {
      let x = colorIndex;
      let xPlusOne = x + 2;

      labels = ["Returning Users", "New Users"];
      const totalReturning = analyticsRecords.reduce(
        (sum, record) => sum + record.returningUsers,
        0,
      );
      const totalNew = analyticsRecords.reduce((sum, record) => sum + record.newUsers, 0);

      dataSets.push({
        label: "Total",
        data: [totalReturning, totalNew],
        borderColor: [getGraphColor(theme, x), getGraphColor(theme, xPlusOne)],
        backgroundColor: [getGraphColor(theme, x), getGraphColor(theme, xPlusOne, 0.5)],
      });

      if (setTitleText) {
        setTitleText(`${totalReturning + totalNew} Users`);
      }
    } else if (graphType === constants.botpressGraphTypes.messagesGraph) {
      labels = extractAnalyticsDateLabels(analyticsRecords);
      let x = colorIndex;
      dataSets.push({
        label: "User Messages",
        data: analyticsRecords.map(record => record.userMessages),
        borderColor: getGraphColor(theme, x),
        backgroundColor: getGraphColor(theme, x++),
      });
      dataSets.push({
        label: "Bot Messages",
        data: analyticsRecords.map(record => record.userMessages),
        borderColor: getGraphColor(theme, x),
        backgroundColor: getGraphColor(theme, x++),
      });
    } else if (graphType === constants.botpressGraphTypes.sessionsGraph) {
      labels = extractAnalyticsDateLabels(analyticsRecords);
      let x = colorIndex;
      dataSets.push({
        label: "Sessions",
        data: analyticsRecords.map(record => record.sessions),
        borderColor: getGraphColor(theme, x),
        backgroundColor: getGraphColor(theme, x++),
      });
    } else if (graphType === constants.botpressGraphTypes.overviewGraph) {
      labels = extractAnalyticsDateLabels(analyticsRecords);

      let x = colorIndex;
      let xPlusOne = x + 1;
      let xPlusTwo = x + 2;
      dataSets.push({
        label: "Total Users",
        data: analyticsRecords.map(record => record.returningUsers + record.newUsers),
        borderColor: getGraphColor(theme, x),
        backgroundColor: context => {
          const { ctx, chartArea } = context.chart;

          if (!chartArea) {
            // This ensures the chart has been initialized before drawing
            return null;
          }

          // Create a vertical gradient (x0, y0, x1, y1)
          const gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);

          // Add color stops (0 is bottom, 1 is top)
          gradient.addColorStop(0, alpha(theme.palette.commonground.main, 1.0)); // Transparent at bottom
          gradient.addColorStop(1, getGraphColor(theme, x, 0.95)); // Semi-opaque at top

          return gradient;
        },
        borderWidth: 2,
        fill: true,
        tension: 0.2,
        pointStyle: false, // Removes point rendering
      });
      dataSets.push({
        label: "Sessions",
        data: analyticsRecords.map(record => record.sessions),
        borderColor: getGraphColor(theme, xPlusOne),
        backgroundColor: context => {
          const { ctx, chartArea } = context.chart;

          if (!chartArea) {
            // This ensures the chart has been initialized before drawing
            return null;
          }

          // Create a vertical gradient (x0, y0, x1, y1)
          const gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);

          // Add color stops (0 is bottom, 1 is top)
          gradient.addColorStop(0, alpha(theme.palette.commonground.main, 1.0)); // Transparent at bottom
          gradient.addColorStop(1, getGraphColor(theme, xPlusOne, 0.75)); // Semi-opaque at top

          return gradient;
        },
        borderWidth: 2,
        fill: true,
        tension: 0.2,
        pointStyle: false, // Removes point rendering
      });
      dataSets.push({
        label: "Messages",
        data: analyticsRecords.map(record => record.userMessages + record.botMessages),
        borderColor: getGraphColor(theme, xPlusTwo),
        backgroundColor: context => {
          const { ctx, chartArea } = context.chart;

          if (!chartArea) {
            // This ensures the chart has been initialized before drawing
            return null;
          }

          // Create a vertical gradient (x0, y0, x1, y1)
          const gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);

          // Add color stops (0 is bottom, 1 is top)
          gradient.addColorStop(0, alpha(theme.palette.commonground.main, 1.0)); // Transparent at bottom
          gradient.addColorStop(1, getGraphColor(theme, xPlusTwo, 0.5)); // Semi-opaque at top

          return gradient;
        },
        borderWidth: 2,
        fill: true,
        tension: 0.2,
        pointStyle: false, // Removes point rendering
      });
    } else if (graphType === constants.botpressGraphTypes.llmGraph) {
      labels = extractAnalyticsDateLabels(analyticsRecords);

      let x = colorIndex;
      let xPlusOne = x + 1;
      let xPlusTwo = x + 2;
      dataSets.push({
        label: "Total Messages",
        data: analyticsRecords.map(record => record.userMessages + record.botMessages),
        borderColor: getGraphColor(theme, x),
        backgroundColor: context => {
          const { ctx, chartArea } = context.chart;

          if (!chartArea) {
            // This ensures the chart has been initialized before drawing
            return null;
          }

          // Create a vertical gradient (x0, y0, x1, y1)
          const gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);

          // Add color stops (0 is bottom, 1 is top)
          gradient.addColorStop(0, alpha(theme.palette.commonground.main, 1.0)); // Transparent at bottom
          gradient.addColorStop(1, getGraphColor(theme, x, 0.95)); // Semi-opaque at top

          return gradient;
        },
        borderWidth: 2,
        fill: true,
        tension: 0.2,
        pointStyle: false, // Removes point rendering
      });
      dataSets.push({
        label: "LLM Calls",
        data: analyticsRecords.map(record => record.llm.calls),
        borderColor: getGraphColor(theme, xPlusOne),
        backgroundColor: context => {
          const { ctx, chartArea } = context.chart;

          if (!chartArea) {
            // This ensures the chart has been initialized before drawing
            return null;
          }

          // Create a vertical gradient (x0, y0, x1, y1)
          const gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);

          // Add color stops (0 is bottom, 1 is top)
          gradient.addColorStop(0, alpha(theme.palette.commonground.main, 1.0)); // Transparent at bottom
          gradient.addColorStop(1, getGraphColor(theme, xPlusOne, 0.75)); // Semi-opaque at top

          return gradient;
        },
        borderWidth: 2,
        fill: true,
        tension: 0.2,
        pointStyle: false, // Removes point rendering
      });
      dataSets.push({
        label: "LLM Errors",
        data: analyticsRecords.map(record => record.llm.errors),
        borderColor: getGraphColor(theme, xPlusTwo),
        backgroundColor: context => {
          const { ctx, chartArea } = context.chart;

          if (!chartArea) {
            // This ensures the chart has been initialized before drawing
            return null;
          }

          // Create a vertical gradient (x0, y0, x1, y1)
          const gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);

          // Add color stops (0 is bottom, 1 is top)
          gradient.addColorStop(0, alpha(theme.palette.commonground.main, 1.0)); // Transparent at bottom
          gradient.addColorStop(1, getGraphColor(theme, xPlusTwo, 0.5)); // Semi-opaque at top

          return gradient;
        },
        borderWidth: 2,
        fill: true,
        tension: 0.2,
        pointStyle: false, // Removes point rendering
      });
    }

    return {
      labels: labels,
      datasets: dataSets,
    };
  };

  const [data, setData] = useState(null);

  const generateOptions = graphType => {
    if (graphType == null) {
      return null;
    }

    switch (graphType) {
      case constants.botpressGraphTypes.usersGraph: {
        return {
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            mode: "index", // Groups all items at the same index (x-axis position)
            intersect: false, // Shows the tooltip even if you aren't hovering directly over a point
          },
          plugins: {
            tooltip: {
              enabled: true,
            },
            legend: {
              position: "top",
            },
            title: {
              display: false,
            },
          },
          scales: {
            y: {
              ticks: {
                // Enforces a step of 1, ensuring only integers are displayed
                stepSize: 1,
                // Alternately, use a callback function to only return whole numbers
                // callback: function(value, index, ticks) {
                //   if (Math.floor(value) === value) {
                //     return value;
                //   }
                // }
              },
            },
          },
        };
      }
      case constants.botpressGraphTypes.userTypesGraph: {
        return {
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            mode: "index", // Groups all items at the same index (x-axis position)
            intersect: false, // Shows the tooltip even if you aren't hovering directly over a point
          },
          plugins: {
            tooltip: {
              enabled: true,
            },
            legend: {
              position: "top",
            },
            title: {
              display: false,
            },
          },
          scales: {
            y: {
              ticks: {
                // Enforces a step of 1, ensuring only integers are displayed
                stepSize: 1,
                // Alternately, use a callback function to only return whole numbers
                // callback: function(value, index, ticks) {
                //   if (Math.floor(value) === value) {
                //     return value;
                //   }
                // }
              },
            },
          },
        };
      }
      case constants.botpressGraphTypes.messagesGraph: {
        return {
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            mode: "index", // Groups all items at the same index (x-axis position)
            intersect: false, // Shows the tooltip even if you aren't hovering directly over a point
          },
          plugins: {
            tooltip: {
              enabled: true,
            },
            legend: {
              position: "top",
            },
            title: {
              display: false,
            },
          },
          scales: {
            x: {
              stacked: true,
            },
            y: {
              stacked: true,
            },
          },
        };
      }
      case constants.botpressGraphTypes.sessionsGraph: {
        return {
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            mode: "index", // Groups all items at the same index (x-axis position)
            intersect: false, // Shows the tooltip even if you aren't hovering directly over a point
          },
          plugins: {
            tooltip: {
              enabled: true,
            },
            legend: {
              position: "top",
            },
            title: {
              display: false,
            },
          },
          scales: {
            y: {
              ticks: {
                // Enforces a step of 1, ensuring only integers are displayed
                stepSize: 1,
                // Alternately, use a callback function to only return whole numbers
                // callback: function(value, index, ticks) {
                //   if (Math.floor(value) === value) {
                //     return value;
                //   }
                // }
              },
            },
          },
        };
      }
      case constants.botpressGraphTypes.overviewGraph: {
        return {
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            mode: "index", // Groups all items at the same index (x-axis position)
            intersect: false, // Shows the tooltip even if you aren't hovering directly over a point
          },
          plugins: {
            tooltip: {
              enabled: true,
            },
            legend: {
              position: "top",
            },
            title: {
              display: false,
            },
          },
          scales: {
            y: {
              ticks: {
                // Enforces a step of 1, ensuring only integers are displayed
                stepSize: 1,
                // Alternately, use a callback function to only return whole numbers
                // callback: function(value, index, ticks) {
                //   if (Math.floor(value) === value) {
                //     return value;
                //   }
                // }
              },
            },
          },
        };
      }
      case constants.botpressGraphTypes.llmGraph: {
        return {
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            mode: "index", // Groups all items at the same index (x-axis position)
            intersect: false, // Shows the tooltip even if you aren't hovering directly over a point
          },
          plugins: {
            tooltip: {
              enabled: true,
            },
            legend: {
              position: "top",
            },
            title: {
              display: false,
            },
          },
          scales: {
            y: {
              ticks: {
                // Enforces a step of 1, ensuring only integers are displayed
                stepSize: 1,
                // Alternately, use a callback function to only return whole numbers
                // callback: function(value, index, ticks) {
                //   if (Math.floor(value) === value) {
                //     return value;
                //   }
                // }
              },
            },
          },
        };
      }
      default:
    }
  };

  const [options, setOptions] = useState(null);

  useEffect(() => {
    setData(generateGraphData(records, botpressGraphType));
    setOptions(generateOptions(botpressGraphType));
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [records, botpressGraphType]);

  if (!data || !options) {
    return <Box sx={{ position: "relative" }}>No data available</Box>;
  }

  console.log("Graph botpressGraphType:", botpressGraphType);
  if (botpressGraphType === constants.botpressGraphTypes.usersGraph) {
    return <Line options={options} data={data} />;
  }

  if (botpressGraphType === constants.botpressGraphTypes.userTypesGraph) {
    return <Doughnut data={data} />;
  }

  if (
    botpressGraphType === constants.botpressGraphTypes.messagesGraph ||
    botpressGraphType === constants.botpressGraphTypes.sessionsGraph
  ) {
    return <Bar options={options} data={data} />;
  }

  if (botpressGraphType === constants.botpressGraphTypes.overviewGraph) {
    return <Line options={options} data={data} />;
  }

  if (botpressGraphType === constants.botpressGraphTypes.llmGraph) {
    return <Line options={options} data={data} />;
  }
};

export default BotpressUseageGraph;
